import React from 'react';
import {useTranslation} from 'react-i18next';
import {animated, useTransition} from 'react-spring';

import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isFunction from 'lodash/isFunction';
import noop from 'lodash/noop';

import KeyboardArrowLeftIcon from '@edna/icons/keyboardArrowLeft.svg';
import {markSearchText} from '@edna/utils';

import LoadByScroll from '../LoadByScroll';
import Spinner from '../Spinner';
import {HighlightText} from '../primitives';
import {useTheme} from '../styles';
import {easing} from '../utils';
import * as S from './style';
import {TSelectOption, TProps as TSelectProps, TValue} from './types';
import {
  DEFAULT_VISIBLE_ELEMENT_COUNT,
  debouncedCall,
  filterOptionBySearch,
  writableProps,
} from './utils';

type TProps = TSelectProps & {
  options: TSelectOption[];
  setSelectedOption: React.Dispatch<React.SetStateAction<TSelectOption | null>>;
  selectedOption: TSelectOption | null;
  propOptions: TSelectOption[];
  isOpen: boolean;
  isPlacementBottom: boolean;
  overlayRef: React.RefObject<HTMLDivElement>;
  setValue: (payload: TValue) => void;
  selectedValues: TValue[];
  selectedOptions: TSelectOption[];
  setOptions: React.Dispatch<React.SetStateAction<TSelectOption[]>>;
  focusIndex: number;
  setFocusIndex: (index: number) => void;
};

const AnimatedOverlay = animated(S.Overlay);

const useOverlayTransitions = (
  isPlacementBottom: boolean,
  isOpen: boolean,
  additional?: TProps['additionalOverlayTransition'],
) => {
  const {
    animation: {duration},
  } = useTheme();

  return useTransition<boolean, React.CSSProperties>(isOpen, {
    from: {opacity: 0, y: isPlacementBottom ? -10 : 10, ...(additional?.from || {})},
    enter: {opacity: 1, y: 0, ...(additional?.enter || {})},
    leave: {opacity: 0, ...(additional?.leave || {})},
    config: {
      duration,
      easing,
      ...(additional?.config || {}),
    },
  });
};

const useSearch = (
  searchInputRef: React.RefObject<HTMLInputElement>,
  {
    propOptions,
    onSearch,
    isOpen,
    setOptions,
    isValueCompareInSearch = writableProps.isValueCompareInSearch,
  }: TProps,
) => {
  const [search, setSearch] = React.useState('');

  React.useEffect(() => {
    if (isOpen) {
      searchInputRef.current?.focus();
    } else {
      setSearch('');
    }
  }, [isOpen, searchInputRef.current]);

  React.useEffect(() => {
    if (isFunction(onSearch)) {
      debouncedCall(() => onSearch(search));
    }
  }, [onSearch, search]);

  React.useEffect(() => {
    if (isOpen) {
      const lowerSearch = search?.toLowerCase();
      const newOptions =
        !isFunction(onSearch) && search
          ? propOptions.filter((option) =>
              filterOptionBySearch(option, lowerSearch, isValueCompareInSearch),
            )
          : propOptions;

      setOptions(newOptions);
    }
  }, [isOpen, isValueCompareInSearch, onSearch, propOptions, search, setOptions]);

  return {search, setSearch};
};

type THighlightProps = {label: string; search: string; disabled?: boolean};

const Highlight = React.memo<THighlightProps>(({label, search, disabled}) => {
  const highlighted = React.useMemo(
    () => markSearchText(HighlightText, label, search),
    [label, search],
  );

  if (disabled) {
    return <div>{label}</div>;
  }

  return <div>{highlighted}</div>;
});

const Overlay = React.memo<TProps>((props) => {
  const {
    visibleElementCount = DEFAULT_VISIBLE_ELEMENT_COUNT,
    optionComponent: OptionComponent = S.Option,
    onChange = noop,
    loadMore,
    onSearch,
    searchPlaceholder,
    loading,
    renderOverlayFooter,
    hasMore,
    multiple,
    renderLabel,
    isPlacementBottom,
    overlayRef,
    isOpen,
    selectedOptions,
    selectedValues,
    options,
    selectedOption,
    setSelectedOption,
    hasExternalSearch,
    focusIndex = -1,
    setFocusIndex,
    additionalOverlayTransition,
  } = props;
  const {t} = useTranslation();

  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const focusRef = React.useRef<HTMLDivElement>(null);

  const {search, setSearch} = useSearch(searchInputRef, props);

  const {
    dimensions: {controlHeightM},
  } = useTheme();

  const hasLazyLoading = !!loadMore;
  const overlayTransitions = useOverlayTransitions(
    isPlacementBottom,
    isOpen,
    additionalOverlayTransition,
  );

  React.useEffect(() => {
    if (focusRef?.current) {
      focusRef.current.scrollIntoView({block: 'nearest'});
    }
  }, [focusIndex]);

  const hasSearch = React.useMemo(() => {
    const {current: overlayNode} = overlayRef;
    const isOverlayLarge =
      overlayNode && overlayNode.clientHeight > visibleElementCount * controlHeightM;
    const isSearchAvailable = onSearch || !!search;
    const optionsCount = options.length;

    return (
      hasExternalSearch || isOverlayLarge || optionsCount > visibleElementCount || isSearchAvailable
    );
  }, [
    hasExternalSearch,
    onSearch,
    options.length,
    overlayRef,
    search,
    controlHeightM,
    visibleElementCount,
  ]);

  const handleInputChange = React.useCallback<React.ChangeEventHandler<HTMLInputElement>>(
    (event) => setSearch(event.target.value),
    [setSearch],
  );

  const renderSearchInput = () => (
    <S.SearchInput>
      <S.Input
        ref={searchInputRef}
        placeholder={searchPlaceholder}
        onChange={handleInputChange}
        value={search}
      />
      <S.SearchIcon />
    </S.SearchInput>
  );

  const handleOptionWithChildrenClick = React.useCallback(
    (option: TSelectOption, event?: React.MouseEvent) => {
      event?.stopPropagation();

      if (option.children) {
        setSelectedOption(option);
        setFocusIndex(-1);
      }
    },
    [],
  );

  const handleNavigateBack = React.useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedOption(null);
  }, []);

  const handleOptionClick = React.useCallback(
    (optionValue: TValue) => {
      const selected = !!selectedValues.find((selectedValue) =>
        isEqual(selectedValue, optionValue),
      );

      if (multiple) {
        onChange(
          selected
            ? selectedValues.filter((selectedValue) => !isEqual(selectedValue, optionValue))
            : [...selectedValues, optionValue],
        );

        return;
      }
      onChange(optionValue);
    },
    [multiple, selectedValues, onChange],
  );

  const getValidLabel = (option: TSelectOption, selected: boolean) => {
    if (renderLabel) {
      return renderLabel({option, search});
    }

    if (typeof option.label === 'string') {
      return (
        <Highlight label={option.label} search={search} disabled={selected || option.disabled} />
      );
    }

    return option.label;
  };

  const renderOption = (option: TSelectOption, index: number) => {
    const selected = !!selectedOptions.find((selectedItem) => isEqual(selectedItem, option));
    const isCurrent = index === focusIndex;
    const hasChildren = !!option.children;

    const allChildrenSelected =
      hasChildren &&
      option.children?.every((child) =>
        selectedValues.find((selectedVal) => isEqual(selectedVal, child.value)),
      );

    return (
      <OptionComponent
        {...option}
        hoverDisabledStyle
        key={JSON.stringify(option.value)}
        selected={selected}
        disabled={selected || option.disabled || allChildrenSelected}
        isCurrent={isCurrent}
        onMouseOver={() => setFocusIndex(index)}
        onMouseMove={() => setFocusIndex(index)}
        innerRef={isCurrent ? focusRef : undefined}
        onClick={
          hasChildren
            ? (_, event) => handleOptionWithChildrenClick(option, event)
            : handleOptionClick
        }
        label={
          <>
            {getValidLabel(option, selected)}
            {hasChildren && <S.ArrowRightIcon />}
          </>
        }
      />
    );
  };

  const renderOptions = () => {
    if (isEmpty(options) && (!hasLazyLoading || !loading)) {
      return (
        <OptionComponent
          key="nothing"
          disabled
          label={t([
            `EdnaSelect:${hasSearch ? 'nothingFound' : 'noData'}`,
            `Select:${hasSearch ? 'nothingFound' : 'noData'}`,
          ])}
        />
      );
    }

    return options.map(renderOption);
  };

  const renderOverlayContent = () => (
    <>
      {selectedOption && (
        <S.BackToParentButton onClick={handleNavigateBack}>
          <KeyboardArrowLeftIcon />
          {selectedOption.label}
        </S.BackToParentButton>
      )}
      {renderOptions()}
      {loading && (
        <S.SpinnerWrapper>
          <Spinner />
        </S.SpinnerWrapper>
      )}
      {renderOverlayFooter && <S.OverlayFooter>{renderOverlayFooter()}</S.OverlayFooter>}
    </>
  );

  const renderOverlay = () => {
    if (hasSearch) {
      return (
        <>
          {isPlacementBottom && renderSearchInput()}
          <S.SearchOptionsWrapper
            visibleElementCount={visibleElementCount}
            ref={scrollContainerRef}
          >
            {hasLazyLoading && scrollContainerRef.current && (
              <LoadByScroll
                reserve={50}
                loadMore={loadMore}
                hasMore={hasMore}
                scrollRef={scrollContainerRef}
              />
            )}
            {renderOverlayContent()}
          </S.SearchOptionsWrapper>
          {!isPlacementBottom && renderSearchInput()}
        </>
      );
    }

    return renderOverlayContent();
  };

  return overlayTransitions(
    (style, state) =>
      state && (
        <AnimatedOverlay
          tabIndex={0}
          style={style}
          hasScroll={!hasSearch}
          isPlacementBottom={isPlacementBottom}
          ref={overlayRef}
        >
          {renderOverlay()}
        </AnimatedOverlay>
      ),
  );
});

export default Overlay;
